import { getExperimentHeaders } from 'api/experiment-headers';
import { AxiosRequestConfig } from 'axios';
import { getRegionConfig } from 'config';
import { getLanguageCode, Locale } from 'config/constants/locales';
import { Region } from 'constants/shop';
import merge from 'lodash/merge';
import { axios } from 'utils/axios';

// Whitelisted paths that should not be prefixed with the API endpoint
const NON_PREFIXED_PATHS: string[] = [];

// https://github.com/sindresorhus/is-absolute-url/blob/master/index.js#L7
const isAbsoluteURLRegex = /^(?:\w+:)\/\//;

let activeRequestInterceptor: number | undefined;
let activeRegion: Region | undefined;
let activeLocale: Locale | undefined;

export const generateApiInterceptor = (
  config: AxiosRequestConfig,
  region: Region = Region.DE,
  locale?: Locale
): AxiosRequestConfig => {
  const isPdexApi = config?.isPdexApi;
  const { apiEndpoint, locale: defaultLocale, pdexEndpoint, shopId } = getRegionConfig(region);

  // Use PDEX endpoint or region-specific API endpoint
  const selectedEndpoint = isPdexApi ? pdexEndpoint : apiEndpoint;

  const newConfig = { ...config };

  // Only modify URL if it's not absolute and not in whitelist
  if (
    newConfig.url &&
    !isAbsoluteURLRegex.test(newConfig.url) &&
    !NON_PREFIXED_PATHS.includes(newConfig.url)
  ) {
    let cleanUrl = newConfig.url.startsWith('/') ? newConfig.url : `/${newConfig.url}`;

    // If Legacy Stack and running locally, replace the first path element with 'local'
    if (!isPdexApi && process.env.BACKEND_ENV === 'local') {
      const pathParts = cleanUrl.split('/');
      if (pathParts.length > 1) {
        pathParts[1] = 'local';
      }
      cleanUrl = pathParts.join('/');
    }

    newConfig.url = `${selectedEndpoint}${cleanUrl}`;
  }

  return merge(newConfig, {
    headers: {
      ...getExperimentHeaders(newConfig),
      ...(!newConfig.headers?.shopid && { shopid: shopId }),
      ...(!newConfig.headers?.market && isPdexApi && { market: shopId }),
      ...(!newConfig.headers?.['accept-language'] && {
        'accept-language': getLanguageCode(locale ?? defaultLocale),
      }),
    },
    withCredentials: true,
  });
};

export const syncApiInterceptor = (region?: Region, locale?: Locale): void => {
  if (typeof window === 'undefined') return;
  if (
    activeRequestInterceptor !== undefined &&
    activeRegion === region &&
    activeLocale === locale
  ) {
    return;
  }

  if (activeRequestInterceptor !== undefined) {
    axios.interceptors.request.eject(activeRequestInterceptor);
  }

  activeRegion = region;
  activeLocale = locale;
  activeRequestInterceptor = axios.interceptors.request.use((config) =>
    generateApiInterceptor(config, region, locale)
  );
};
